import { ComponentType, useEffect, useState } from 'react';
import RcInstanceDTO from '@robocorp/robocloud-internal-cds-sdk/lib/sdk/Types';

import { useAuth } from '~/auth/AuthProvider';
import { assertNoRedirectLoop } from '~/utils/redirectLoopDetector';
import { getBaseUrl } from '~/utils/cloudHelper';
import { useCurrentUser } from '~/queries';
import ViewLoader from '../components/ViewLoader';
import { buildRedirectParams } from '../utils/buildRedirectUrl';
import { redirectToIdSite } from '../utils/redirectToIdSite';
import { useSessionStorage } from '../hooks';

const AuthErrorToUrl: Record<string, string> = {
  EmailNotVerified: 'email-not-verified',
  UserNotActive: 'user-not-active', // TODO this is legacy from invite-only website
};

const isIdSiteOnboardingMissing = (user: RcInstanceDTO.User): boolean =>
  !user.details.onboarding_ts;

export const withUser = (WrappedComponent: ComponentType) => () => {
  const { logout } = useAuth();
  const [initialized, setInitialized] = useState<boolean>(false);

  const {
    setStorageValue: setUserLoginInit,
    removeStorageValue: removeUserLoginInit,
    storageValue: userLoginInit,
  } = useSessionStorage<undefined | number>({
    key: 'USER_LOGIN_INIT',
    initialValue: undefined,
  });

  /**
   * On app initialization send logged in event by passing "login=true" here
   * To not spam this on each app refresh login init state is stored in session storage
   */
  const { refetch: getCurrentUser } = useCurrentUser({
    login: !userLoginInit || undefined,
    enabled: false,
  });

  useEffect(() => {
    const initUser = async () => {
      const url = new URL(window.location.href);

      const currentUser = await getCurrentUser();
      url.searchParams.delete('login');

      if (currentUser.data) {
        window.history.replaceState('', document.title, url.href);

        if (isIdSiteOnboardingMissing(currentUser.data)) {
          const { redirectUrl } = buildRedirectParams();
          redirectToIdSite(redirectUrl, 'ONBOARDING');
          return;
        }

        if (!userLoginInit) {
          setUserLoginInit(Date.now());
        }

        setInitialized(true);
      } else {
        const errorCode = currentUser?.error?.subCode;
        window.localStorage.removeItem('currentOrganizationShortName');
        window.localStorage.removeItem('currentWorkspaceShortName');
        assertNoRedirectLoop('missing user data');
        removeUserLoginInit();

        const baseUrl = getBaseUrl();

        logout({
          returnTo: `${baseUrl}/signout-callback?targetState=${AuthErrorToUrl[errorCode || '']}`,
        });
      }
    };

    initUser();
  }, []);

  return !initialized ? <ViewLoader key="initial" initial /> : <WrappedComponent />;
};
